import LgpdNotice from '@/components/feature/LgpdNotice';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { portfolioFaqItems } from '@/mocks/faq';
import { galleryImages } from '@/mocks/gallery';
import InstagramReels from '@/components/feature/InstagramReels';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

/* Instituições e empresas que já contrataram a STIB. Lista confirmada pelo Daniel. */
const CLIENTES = [
  'Governo Federal', 'Embaixada Brasileira', 'Polo Brasília', 'SESI', 'UNESCO',
  'Polícia Federal', 'Governo de São Paulo', 'TCU', 'SEBRAE', 'BID', 'Finatec',
  'UnB', 'Ministério da Saúde', 'Porto Digital', 'UNODC', 'Embrapa', 'STF',
  'Conselho Nacional de Justiça', 'Ministério da Justiça',
];

export default function PortfolioPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [selectedFaqCategory, setSelectedFaqCategory] = useState('Equipamentos');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  /* form */
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', eventType: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: clientesRef, isVisible: clientesVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: galleryRef, isVisible: galleryVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: faqHeaderRef, isVisible: faqHeaderVisible } = useScrollAnimation();
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.05 });

  const faqCategories = ['Equipamentos', 'Serviços', 'Logística', 'Preços'];
  const filteredFaqs = portfolioFaqItems.filter((item) => item.category === selectedFaqCategory);

  const openLightbox = (i: number) => {
    setCurrentImageIndex(i);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  /* faq */
  const toggleFaq = (i: number) => setOpenFaqIndex(openFaqIndex === i ? null : i);

  /* form */
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://ordem-de-servico.stibpalestrasilenciosasp.com.br/pedido.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitMessage('Solicitação enviada com sucesso! Entraremos em contato em breve.');
        setFormData({ name: '', email: '', phone: '', eventType: '', message: '' });
      } else setSubmitMessage('Erro ao enviar. Por favor, tente novamente.');
    } catch {
      setSubmitMessage('Erro ao enviar. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 8000);
    }
  };

  /* SEO */
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';
    document.title = 'Portfólio | STIB Palestra Silenciosa São Paulo | Clientes Atendidos';
    document.querySelector('meta[name="description"]')?.setAttribute('content', `Clientes atendidos pela STIB Palestra Silenciosa São Paulo: órgãos públicos, organismos internacionais, universidades e empresas. Mais de 500 eventos em todo o Brasil.`);
    document.querySelector('meta[name="last-modified"]')?.setAttribute('content', new Date().toISOString().split('T')[0]);

    const schemas = [
      {
        id: 'portfolio-collection-schema',
        schema: {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Portfólio - STIB Palestra Silenciosa São Paulo',
          url: `${siteUrl}/portfolio`,
          inLanguage: 'pt-BR',
          provider: { '@type': 'LocalBusiness', name: 'STIB - Palestra Silenciosa São Paulo' },
        },
      },
      {
        id: 'portfolio-breadcrumb-schema',
        schema: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Portfólio', item: `${siteUrl}/portfolio` },
          ],
        },
      },
    ];

    schemas.forEach(({ id, schema }) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = id;
      el.text = JSON.stringify(schema);
      document.head.appendChild(el);
    });
    return () => schemas.forEach(({ id }) => document.getElementById(id)?.remove());
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ── Hero ── */}
      <section ref={heroRef} className="relative h-[440px] flex items-center justify-center overflow-hidden mt-[72px]">
        <div className="absolute inset-0">
          <img
            src="/img/stib/evento-palestra-silenciosa-plateia-feira-evento-f.webp"
            alt="Nosso Portfólio"
            className={`w-full h-full object-cover object-top transition-all duration-1000 ${heroVisible ? 'scale-100' : 'scale-110'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-secondary/65 via-secondary/50 to-secondary/70"></div>
        </div>

        <div className="absolute top-6 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6">
            <Breadcrumb variant="light" items={[{ label: 'Home', to: '/' }, { label: 'Portfólio' }]} />
          </div>
        </div>

        <div className="relative z-10 text-center px-6 w-full max-w-4xl mx-auto">
          <p className={`font-body text-primary font-semibold text-sm uppercase tracking-widest mb-3 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            +500 eventos realizados
          </p>
          <h1 className={`font-heading font-bold text-5xl md:text-7xl text-white mb-5 transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            NOSSO PORTFÓLIO
          </h1>
          <p className={`font-body text-lg text-white/85 max-w-2xl mx-auto mb-8 transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            Quem já confiou na palestra silenciosa da STIB, em todo o Brasil.
          </p>
          <div className={`flex flex-wrap items-center justify-center gap-4 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {[
              { icon: 'ri-history-line', label: 'Desde 2007' },
              { icon: 'ri-calendar-check-line', label: '+500 eventos' },
              { icon: 'ri-map-pin-line', label: 'Cobertura nacional' },
            ].map((b) => (
              <div key={b.label} className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-4 py-2 rounded-full font-body text-sm text-white font-medium">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${b.icon} text-primary text-sm`}></i>
                </div>
                {b.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Clientes atendidos ── */}
      <section className="py-20 bg-white">
        <div ref={clientesRef} className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-12 transition-all duration-700 ${clientesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="font-body text-primary font-semibold text-sm uppercase tracking-widest mb-3 block">Nossos clientes</span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary mb-4">CLIENTES ATENDIDOS</h2>
            <p className="font-body text-gray-500 max-w-2xl mx-auto">
              Órgãos públicos, organismos internacionais, universidades e empresas que já realizaram
              eventos com a nossa tecnologia de áudio sem fio.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {CLIENTES.map((nome, i) => (
              <span
                key={nome}
                className={`inline-flex items-center px-5 py-2.5 rounded-full bg-gray-50 border border-gray-200 font-body text-sm font-semibold text-secondary transition-all duration-500 hover:border-primary hover:bg-primary/5 ${clientesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: clientesVisible ? `${i * 40}ms` : '0ms' }}
              >
                {nome}
              </span>
            ))}
          </div>
        </div>
      </section>


      {/* ── CTA mid-page ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="/img/stib/evento-plateia-fones-sem-fio-palco-congresso-f.webp"
              alt="Quero um evento assim"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-secondary/90 to-secondary/60"></div>
            <div className="relative z-10 py-14 px-10 md:px-16 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <p className="font-body text-primary font-semibold text-sm uppercase tracking-widest mb-2">Próximo passo</p>
                <h2 className="font-heading font-bold text-4xl text-white mb-3">
                  Quero um evento assim
                </h2>
                <p className="font-body text-white/80 text-base max-w-lg">
                  Realizamos eventos corporativos, congressos médicos, tecnologia, cultura e muito mais em todo o Brasil.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
                <a
                  href="https://wa.me/5511917776227?text=Olá! Vi o portfólio da STIB e quero saber mais."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-body font-bold px-7 py-3.5 rounded-full transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-lg"></i>
                  </div>
                  Falar no WhatsApp
                </a>
                <Link
                  to="/contato"
                  className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white font-body font-bold px-7 py-3.5 rounded-full transition-all hover:scale-105 whitespace-nowrap cursor-pointer border border-white/30"
                >
                  Solicitar orçamento
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

        <InstagramReels />

      {/* ── FAQ ── */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div ref={faqHeaderRef} className={`text-center mb-12 transition-all duration-700 ${faqHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="font-heading font-bold text-5xl text-secondary mb-4">PERGUNTAS FREQUENTES</h2>
            <p className="font-body text-gray-500">Tudo o que você precisa saber sobre nossos serviços</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {faqCategories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => { setSelectedFaqCategory(cat); setOpenFaqIndex(null); }}
                className={`px-6 py-2 rounded-full font-body font-semibold text-sm cursor-pointer whitespace-nowrap transition-all duration-200 ${
                  selectedFaqCategory === cat
                    ? 'bg-secondary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div ref={faqRef} className="space-y-0 border-t border-gray-100">
            {filteredFaqs.map((item, i) => (
              <div
                key={item.id}
                className={`border-b border-gray-100 transition-all duration-500 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: faqVisible ? `${i * 70}ms` : '0ms' }}
              >
                <button onClick={() => toggleFaq(i)} className="w-full py-6 px-4 flex items-center justify-between text-left cursor-pointer group">
                  <h3 className="font-heading font-semibold text-lg text-secondary pr-8 group-hover:text-primary transition-colors">{item.question}</h3>
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 group-hover:bg-primary/10 transition-colors flex-shrink-0">
                    <i className={`${openFaqIndex === i ? 'ri-subtract-line' : 'ri-add-line'} text-primary text-lg transition-transform duration-200 ${openFaqIndex === i ? 'rotate-0' : ''}`}></i>
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaqIndex === i ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="px-4 pb-6">
                    <p className="font-body text-sm text-gray-600 leading-relaxed">{item.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact form ── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="bg-secondary p-12 lg:p-16 text-white">
                <span className="font-body text-primary font-semibold text-sm uppercase tracking-widest mb-4 block">Quer um evento assim?</span>
                <h2 className="font-heading font-bold text-4xl lg:text-5xl mb-5">Transforme Seu Próximo Evento</h2>
                <p className="font-body text-white/75 leading-relaxed mb-10">
                  Inspirado pelos nossos cases de sucesso? Entre em contato e descubra como podemos criar uma experiência única para o seu evento em qualquer cidade do Brasil.
                </p>
                <div className="space-y-5">
                  <a href="mailto:contato@stibpalestrasilenciosasp.com.br" className="flex items-center gap-4 group cursor-pointer">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 group-hover:bg-primary transition-all">
                      <i className="ri-mail-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="font-body text-xs text-white/50 mb-0.5">E-mail</p>
                      <p className="font-body font-semibold text-sm">contato@stibpalestrasilenciosasp.com.br</p>
                    </div>
                  </a>
                  <a href="https://wa.me/5511917776227" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group cursor-pointer">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 group-hover:bg-green-500 transition-all">
                      <i className="ri-whatsapp-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="font-body text-xs text-white/50 mb-0.5">WhatsApp</p>
                      <p className="font-body font-semibold text-sm">(11) 91777-6227</p>
                    </div>
                  </a>
                </div>
                <div className="mt-10 pt-8 border-t border-white/15">
                  <div className="flex flex-wrap gap-2">
                    {['São Paulo', 'Rio de Janeiro', 'Brasília', 'Belo Horizonte', 'Curitiba'].map((city) => (
                      <span key={city} className="inline-flex items-center gap-1 bg-white/10 text-white/80 px-3 py-1 rounded-full font-body text-xs">
                        <i className="ri-map-pin-line text-primary text-xs"></i>
                        {city}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-12 lg:p-16">
                <h3 className="font-heading font-bold text-3xl text-secondary mb-8">Solicite um Orçamento</h3>
                <form onSubmit={handleFormSubmit} data-readdy-form id="portfolio-contact-form">
                  <div className="space-y-5">
                    <div>
                      <label className="block font-body text-sm font-semibold text-gray-700 mb-2">Nome Completo *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleFormChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none font-body text-sm bg-white text-gray-800" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block font-body text-sm font-semibold text-gray-700 mb-2">E-mail *</label>
                        <input type="email" name="email" value={formData.email} onChange={handleFormChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none font-body text-sm bg-white text-gray-800" />
                      </div>
                      <div>
                        <label className="block font-body text-sm font-semibold text-gray-700 mb-2">Telefone *</label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none font-body text-sm bg-white text-gray-800" />
                      </div>
                    </div>
                    <div>
                      <label className="block font-body text-sm font-semibold text-gray-700 mb-2">Tipo de Evento *</label>
                      <select name="eventType" value={formData.eventType} onChange={handleFormChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none font-body text-sm bg-white text-gray-800">
                        <option value="">Selecione...</option>
                        <option value="Evento Corporativo">Evento Corporativo</option>
                        <option value="Conferência">Conferência</option>
                        <option value="Workshop">Workshop</option>
                        <option value="Congresso Médico">Congresso Médico</option>
                        <option value="Feira">Feira</option>
                        <option value="Silent Disco">Silent Disco</option>
                        <option value="Outro">Outro</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-body text-sm font-semibold text-gray-700 mb-2">Conte-nos sobre seu evento</label>
                      <textarea name="message" value={formData.message} onChange={handleFormChange} maxLength={500} rows={4} placeholder="Data prevista, número de participantes, cidade, local..." className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none font-body text-sm resize-none bg-white text-gray-800"></textarea>
                      <p className="text-xs text-gray-400 mt-1">{formData.message.length}/500</p>
                    </div>
                    <LgpdNotice kind="orcamento" className="mb-4 col-span-full" />
            <button type="submit" disabled={isSubmitting} className="w-full bg-primary text-white px-8 py-4 rounded-full font-body font-bold text-sm hover:bg-primary/90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-50">
                      {isSubmitting ? 'ENVIANDO...' : 'ENVIAR SOLICITAÇÃO'}
                    </button>
                    {submitMessage && (
                      <p className={`text-center font-body text-sm ${submitMessage.includes('sucesso') ? 'text-green-600' : 'text-red-500'}`}>
                        {submitMessage}
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}